import { Injectable } from '@angular/core';
import { createEffect, ofType, Actions } from '@ngrx/effects';
import { Store } from '@ngrx/store';
// import { AppState } from '@capacitor/app';
import { setLoadingSpinner } from '../actions/shared.action';
import { catchError, exhaustMap, filter, map, mergeMap, of, tap } from 'rxjs';
import { ROUTER_NAVIGATION, RouterNavigatedAction } from '@ngrx/router-store';
import * as fromPaymentActions from '../actions/payment.action'
import { APIResponse } from 'src/app/models/api-response.model';
import { AppState } from '../app.state';
import { PaymentService } from 'src/app/services/payment.service';
import { ModalService } from 'src/app/services/modal.service';
import { Router } from '@angular/router';
import { getStudentAssessment, getStudentLatestAssessment } from '../actions/student.action';

@Injectable()
export class PaymentEffects {

  getPaymentMethods$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ROUTER_NAVIGATION),
      filter((r : RouterNavigatedAction) => {
        return r.payload.routerState.url.startsWith('/home/payment-methods')
        || r.payload.routerState.url.startsWith('/home/registrar/assessment/payment-method');
      }),
      mergeMap(() => {
        return this.paymentService.getPaymentMethods().pipe(
          map((response: APIResponse) => {
            return fromPaymentActions.getPaymentMethodsSuccess({ data: response?.details});
          })
        );
      })
    );
  });

getPaymentTypes$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ROUTER_NAVIGATION),
      filter((r : RouterNavigatedAction) => {
        return r.payload.routerState.url.startsWith('/home/payment-types');
      }),
      mergeMap(() => {
        return this.paymentService.getPaymentTypes().pipe(
          map((response: APIResponse) => {
            return fromPaymentActions.getPaymentTypesSuccess({ data: response?.details});
          })
        );
      })
    );
  });
  
  getPaymentTerms$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ROUTER_NAVIGATION),
      filter((r : RouterNavigatedAction) => {
        return r.payload.routerState.url.startsWith('/home/payment-terms');
      }),
      mergeMap(() => {
        return this.paymentService.getPaymentTerms().pipe(
          map((response: APIResponse) => {
            return fromPaymentActions.getPaymentTermsSuccess({ data: response?.details});
          })
        );
      })
    );
  });

  getPaymentSchemes$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ROUTER_NAVIGATION),
      filter((r : RouterNavigatedAction) => {
        return r.payload.routerState.url.startsWith('/home/payment-schemes')
        || r.payload.routerState.url.startsWith('/home/registrar/assessment/view-student');
      }),
      mergeMap(() => {
        return this.paymentService.getPaymentSchemes().pipe(
          map((response: APIResponse) => {
            return fromPaymentActions.getPaymentSchemesSuccess({ data: response?.details});
          })
        );
      })
    );
  });

  getPaymentSchemesDetails$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ROUTER_NAVIGATION),
      filter((r : RouterNavigatedAction) => {
        return r.payload.routerState.url.startsWith('/home/payment-schemes-details');
      }),
      mergeMap(() => {
        return this.paymentService.getPaymentSchemesDetails().pipe(
          map((response: APIResponse) => {
            return fromPaymentActions.getPaymentSchemeDetailsSuccess({ data: response?.details});
          })
        );
      })
    );
  });
   
  choosePaymentScheme$ = createEffect(
    ()=> this.actions$.pipe(
      ofType(fromPaymentActions.choosePaymentScheme),
      exhaustMap((action) => 
        this.paymentService.choosePaymentScheme(action?.assessment_id, action?.data).pipe(
          map((response)=>{
            return fromPaymentActions.choosePaymentSchemeSuccess({assessment_id: action?.assessment_id, data: response?.message, student_id: action?.student_id })
          }),
          catchError((response)=>{
            this.store.dispatch(setLoadingSpinner({status : false}));
            return of(fromPaymentActions.choosePaymentSchemeFailed({data : response?.error?.message}))
          })
        )
      )
    )
  );
  
  choosePaymentSchemeSuccess$ = createEffect(
    ()=> this.actions$.pipe(
        ofType(fromPaymentActions.choosePaymentSchemeSuccess),
        tap(async (action) =>{
          const message = {
            message: action?.data
          }
          this.modalService.successModal(message);
          this.store.dispatch(getStudentLatestAssessment({student_id: action?.student_id}))
        })
      ),
    { dispatch: false }
  );

  choosePaymentSchemeFailed$ = createEffect(
    ()=> this.actions$.pipe(
        ofType(fromPaymentActions.choosePaymentSchemeFailed),
        tap(async (action) =>{
          if(action.data){
            this.modalService.errorModal(action?.data)
          } else{
            const message = 'Unkown error ocurred. Please try again later.'
            this.modalService.errorModal(message)
          }
        })
      ),
    { dispatch: false }
  );

  previewPaymentScheme$ = createEffect(
    ()=> this.actions$.pipe(
      ofType(fromPaymentActions.previewPaymentScheme),
      exhaustMap((action) => 
        this.paymentService.previewPaymentScheme(action?.assessment_id, action?.data).pipe(
          map((response)=>{
            return fromPaymentActions.previewPaymentSchemeSuccess({assessment_id: action?.assessment_id, data: response?.details })
          }),
          catchError((response)=>{
            this.store.dispatch(setLoadingSpinner({status : false}));
            return of(fromPaymentActions.previewPaymentSchemeFailed({data : response?.error?.message}))
          })
        )
      )
    )
  );
  
  previewPaymentSchemeSuccess$ = createEffect(
    ()=> this.actions$.pipe(
        ofType(fromPaymentActions.previewPaymentSchemeSuccess),
        tap(async (action) =>{
          this.store.dispatch(setLoadingSpinner({status: false}));
          // this.store.dispatch(getStudentAssessment({assessment_id: action?.assessment_id}))
        })
      ),
    { dispatch: false }
  );

  previewPaymentSchemeFailed$ = createEffect(
    ()=> this.actions$.pipe(
        ofType(fromPaymentActions.previewPaymentSchemeFailed),
        tap(async (action) =>{
          if(action.data){
            this.modalService.errorModal(action?.data)
          } else{
            const message = 'Unkown error ocurred. Please try again later.'
            this.modalService.errorModal(message)
          }
        })
      ),
    { dispatch: false }
  );

  getStudentPaymentBalance$ = createEffect(
    ()=> this.actions$.pipe(
      ofType(fromPaymentActions.getStudentPaymentBalance),
      exhaustMap((action) => 
        this.paymentService.getStudentPaymentBalance(action?.student_id).pipe(
          map((response)=>{
            const data = {
              assessment_id: action?.assessment_id,
              details: response?.details
            }
            return fromPaymentActions.getStudentPaymentBalanceSuccess({data, redirect: action?.redirect })
          }),
          catchError((response)=>{
            this.store.dispatch(setLoadingSpinner({status : false}));
            return of(fromPaymentActions.getStudentPaymentBalanceFailed({data : response?.error?.message}))
          })
        )
      )
    )
  );
  
  getStudentPaymentBalanceSuccess$ = createEffect(
    ()=> this.actions$.pipe(
        ofType(fromPaymentActions.getStudentPaymentBalanceSuccess),
        tap(async (action) =>{
          if(action?.redirect){
          this.router.navigateByUrl('/student/payment/upload-payment')
          }
          this.store.dispatch(setLoadingSpinner({status: false}));
        })
      ),
    { dispatch: false }
  );

  getStudentPaymentBalanceFailed$ = createEffect(
    ()=> this.actions$.pipe(
        ofType(fromPaymentActions.getStudentPaymentBalanceFailed),
        tap(async (action) =>{
          if(action.data){
            this.modalService.errorModal(action?.data)
          } else{
            const message = 'Unkown error ocurred. Please try again later.'
            this.modalService.errorModal(message)
          }
        })
      ),
    { dispatch: false }
  );

  uploadPayment$ = createEffect(
    ()=> this.actions$.pipe(
      ofType(fromPaymentActions.uploadPayment),
      exhaustMap((action) => 
        this.paymentService.uploadPayment(action?.assessment_id, action?.data).pipe(
          map((response)=>{
            return fromPaymentActions.uploadPaymentSuccess({assessment_id: action?.assessment_id, data: response?.details })
          }),
          catchError((response)=>{
            this.store.dispatch(setLoadingSpinner({status : false}));
            return of(fromPaymentActions.uploadPaymentFailed({data : response?.error?.message}))
          })
        )
      )
    )
  );
  
  uploadPaymentSuccess$ = createEffect(
    ()=> this.actions$.pipe(
        ofType(fromPaymentActions.uploadPaymentSuccess),
        tap(async (action) =>{
          this.store.dispatch(setLoadingSpinner({status: false}));
        })
      ),
    { dispatch: false }
  );

  uploadPaymentFailed$ = createEffect(
    ()=> this.actions$.pipe(
        ofType(fromPaymentActions.uploadPaymentFailed),
        tap(async (action) =>{
          if(action.data){
            this.modalService.errorModal(action?.data)
          } else{
            const message = 'Unkown error ocurred. Please try again later.'
            this.modalService.errorModal(message)
          }
        })
      ),
    { dispatch: false }
  );

  getPaymentList$ = createEffect(
    ()=> this.actions$.pipe(
      ofType(fromPaymentActions.getPaymentList),
      exhaustMap((action) => 
        this.paymentService.getPaymentList(action?.data).pipe(
          map((response)=>{
            this.store.dispatch(setLoadingSpinner({status: false}))

            this.router.navigateByUrl('/home/finance/cashier/payment-list')
            return fromPaymentActions.getPaymentListSuccess({data: response?.details ? response?.details : response })
          }),
          catchError((response)=>{
            this.store.dispatch(setLoadingSpinner({status : false}));
            return of(fromPaymentActions.getPaymentListFailed({data : response?.error?.message}))
          })
        )
      )
    )
  );

  approvePayment$ = createEffect(
    ()=> this.actions$.pipe(
      ofType(fromPaymentActions.approvePayment),
      exhaustMap((action) => 
        this.paymentService.approvePayment(action?.studentPayment_id, action?.data).pipe(
          map((response)=>{
            return fromPaymentActions.approvePaymentSuccess({ data: response?.details })
          }),
          catchError((response)=>{
            this.store.dispatch(setLoadingSpinner({status : false}));
            return of(fromPaymentActions.approvePaymentFailed({data : response?.error?.message}))
          })
        )
      )
    )
  );
  
  approvePaymentSuccess$ = createEffect(
    ()=> this.actions$.pipe(
      ofType(fromPaymentActions.approvePaymentSuccess),
      tap(async (action) =>{
          const message = {
          message: 'Payment Approved'
        }
        this.modalService.successModal(message);
        this.store.dispatch(fromPaymentActions.getPaymentList({data: ''}));
      })
    ),
    { dispatch: false }
  );

  approvePaymentFailed$ = createEffect(
    ()=> this.actions$.pipe(
        ofType(fromPaymentActions.approvePaymentFailed),
        tap(async (action) =>{
          if(action.data){
            this.modalService.errorModal(action?.data)
          } else{
            const message = 'Unkown error ocurred. Please try again later.'
            this.modalService.errorModal(message)
          }
        })
      ),
    { dispatch: false }
  );

  constructor(
    private actions$: Actions,
    private paymentService: PaymentService,
    private store: Store<AppState>,
    private modalService: ModalService,
    private router: Router
  ) {}

}
